import { useParams } from "react-router-dom";
import React, { useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetWaterLevelAPIQuery } from "../../redux/services/apiHome";
import DateFilter from "../../components/DateFilter";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";
import { resetSubmitDate, setSubmitDate } from "../../redux/slice/sliceHome";
import usePagination from "../../hooks/usePagination";
import Pagination from "../../components/Pagination/Pagination";
import { useGetProtectedApiDataQuery } from "../../redux/services/apiSlice";
// import { parseDateFormat } from "../../Utils/utils";
import PageLastUpdated from "../../components/shared/PageLastUpdated";
import { useTableResponsive } from "../../hooks/useTableResponsive";

const WaterLevelsDetailPage = () => {
  const { languageType, submitDate } = useSelector((state) => state.homeSlice);
  const dispatch = useDispatch();
  const { id } = useParams();
  const { getText } = useLanguageTranslation();

  function formatDateToYYYYMMDD() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const newFormattedDate = formatDateToYYYYMMDD();

  useEffect(() => {
    dispatch(setSubmitDate(newFormattedDate));
    return () => dispatch(resetSubmitDate());
  }, [newFormattedDate, dispatch]); // Add dependency

  const {
    data: fetchedData = [],
    error: waterLevelError,
    isLoading: isWaterLevelLoading,
  } = useGetWaterLevelAPIQuery(
    submitDate && id
      ? {
          date: submitDate,
          damId: id,
        }
      : skipToken,
  );

  const getLatestDamRecord = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;

    let latestDate = arr[0].field_date;

    for (let i = 1; i < arr.length; i++) {
      if (arr[i].field_date > latestDate) {
        latestDate = arr[i].field_date;
      }
    }

    // convert YYYY-MM-DD → DD-MM-YYYY
    const [yyyy, mm, dd] = latestDate.split("-");
    return `${dd}-${mm}-${yyyy}`;
  };

  const latestDate = getLatestDamRecord(fetchedData);

  const { data: reservoirList = [] } = useGetProtectedApiDataQuery({
    url: "reservoir-status",
    lang: languageType,
  });
  const reservoirObj = reservoirList.find((item) => item.field_path === id);

  useTableResponsive(fetchedData);

  const { currentItems, currentPage, totalPages, handlePageChange } =
    usePagination(fetchedData, 15);

  return (
    <>
      <InnerBannerBreadcrumb
        bannerImage="/images/inner_aboutbann.jpg"
        title={getText("Reservoir Status", "जलाशय स्थिति")}
        titleLink="/reservoir-status"
        titleLink2={"/reservoir-status"}
        titleTwo={getText("Water Levels", "जल स्तर")}
        titleThree={reservoirObj?.title}
        levelTwo={true}
        levelThree={true}
        isActive={reservoirObj?.title}
      />

      <div className="inner-container">
        <div className="container">
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6 col-lg-6 mb-3 justify-content-between">
                <DateFilter />
              </div>
              <div className="col-12 col-md-6 col-lg-6 mb-3 justify-content-end">
                {fetchedData?.length > 0 && latestDate !== null && (
                  <PageLastUpdated lastUpdatedOn={latestDate} />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                {isWaterLevelLoading ? (
                  <div className="text-center">
                    {getText("Loading data...", "डेटा लोड हो रहा है...")}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped custom-table">
                      <thead className="thead-dark">
                        {/* Row 1 */}
                        <tr>
                          {/* Time merged vertically */}
                          <th rowSpan={2} className="align-middle">
                            {getText("Time", "समय")}
                            <br />({getText("HH:MM", "घंटे:मिनट")})
                          </th>

                          {/* Water Level merged vertically */}
                          <th rowSpan={2} className="align-middle">
                            {getText(
                              "Water Level (in meter)",
                              "जल स्तर (मीटर में)",
                            )}
                          </th>

                          {/* Spilling Status merged vertically */}
                          <th rowSpan={2} className="align-middle">
                            {getText("Spilling Status", "स्पिलिंग स्थिति")}
                          </th>

                          {/* Gate Positions merged horizontally */}
                          <th colSpan={6} className="text-center gate-cell">
                            {getText("Gate Positions", "गेट स्थिति")}
                          </th>

                          {/* Inflow merged vertically */}
                          <th rowSpan={2} className="align-middle">
                            {getText(
                              "Inflow in River (cumecs)",
                              "नदी में प्रवाह (क्यूमेक्स)",
                            )}
                          </th>

                          {/* Discharge merged vertically */}
                          <th rowSpan={2} className="align-middle">
                            {getText(
                              "Discharge through gates (cumecs)",
                              "गेट के माध्यम से निर्वहन (क्यूमेक्स)",
                            )}
                          </th>

                          {/* Remarks merged vertically */}
                          <th rowSpan={2} className="align-middle">
                            {getText("Remarks", "टिप्पणियाँ")}
                          </th>
                        </tr>

                        {/* Row 2 → Gate numbers */}
                        <tr>
                          <th>{getText("Gate 1", "गेट 1")}</th>
                          <th>{getText("Gate 2", "गेट 2")}</th>
                          <th>{getText("Gate 3", "गेट 3")}</th>
                          <th>{getText("Gate 4", "गेट 4")}</th>
                          <th>{getText("Gate 5", "गेट 5")}</th>
                          <th className="gateCell">
                            {getText("Gate 6", "गेट 6")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {fetchedData.length === 0 || waterLevelError ? (
                          // Show "No records found" in case of error or no data
                          <tr className="text-center">
                            <td colSpan={12}>
                              {getText(
                                "No records found",
                                "कोई रिकॉर्ड नहीं मिला",
                              )}
                            </td>
                          </tr>
                        ) : (
                          (currentItems || []).map((item, index) => (
                            <tr key={index}>
                              <td data-key={getText("Time", "समय")}>
                                {item.field_time}
                              </td>
                              <td
                                data-key={getText(
                                  "Water Level (in meter)",
                                  "जल स्तर (मीटर में)",
                                )}
                              >
                                {item.field_water_level}
                              </td>

                              <td
                                data-key={getText(
                                  "Spilling Status",
                                  "स्पिलिंग स्थिति",
                                )}
                              >
                                {item.field_dam_spilling_status === "_none"
                                  ? ""
                                  : item.field_dam_spilling_status ===
                                      "not_spilling"
                                    ? "Not Spilling"
                                    : item.field_dam_spilling_status}
                              </td>
                              <td
                                data-key={getText("Gate 1", "गेट 1")}
                                className="text-center"
                              >
                                {item.field_list_gate_1}
                                {item.field_gate_1 && (
                                  <span className="span-gate">
                                    {item.field_gate_1}
                                  </span>
                                )}
                              </td>
                              <td
                                data-key={getText("Gate 2", "गेट 2")}
                                className="text-center"
                              >
                                {item.field_list_gate_2}
                                {item.field_gate_2 && (
                                  <span className="span-gate">
                                    {item.field_gate_2}
                                  </span>
                                )}
                              </td>

                              <td
                                data-key={getText("Gate 3", "गेट 3")}
                                className="text-center"
                              >
                                {item.field_list_gate_3}
                                {item.field_gate_3 && (
                                  <span className="span-gate">
                                    {item.field_gate_3}
                                  </span>
                                )}
                              </td>
                              <td
                                data-key={getText("Gate 4", "गेट 4")}
                                className="text-center"
                              >
                                {item.field_list_gate_4}
                                {item.field_gate_4 && (
                                  <span className="span-gate">
                                    {item.field_gate_4}
                                  </span>
                                )}
                              </td>
                              <td
                                data-key={getText("Gate 5", "गेट 5")}
                                className="text-center"
                              >
                                {item.field_list_gate_5}
                                {item.field_gate_5 && (
                                  <span className="span-gate">
                                    {item.field_gate_5}
                                  </span>
                                )}
                              </td>
                              <td
                                data-key={getText("Gate 6", "गेट 6")}
                                className="text-center"
                              >
                                {item.field_list_gate_6}
                                {item.field_gate_6 && (
                                  <span className="span-gate">
                                    {item.field_gate_6}
                                  </span>
                                )}
                              </td>
                              <td
                                data-key={getText(
                                  "Inflow in River (cumecs)",
                                  "नदी में प्रवाह (क्यूमेक्स)",
                                )}
                              >
                                {item.inflow_in_river
                                  ? item.inflow_in_river
                                  : item.field_in_flow}
                              </td>
                              <td
                                data-key={getText(
                                  "Discharge through gates (cumecs)",
                                  "गेट के माध्यम से निर्वहन (क्यूमेक्स)",
                                )}
                              >
                                {item.field_discharge_in_cumecs}
                              </td>
                              <td data-key={getText("Remarks", "टिप्पणियाँ")}>
                                {item.field_dam_remark}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {fetchedData.length !== 0 && (
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WaterLevelsDetailPage;
